import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { Metadata, RunView } from '@memberjunction/core';
import { Subject } from 'rxjs';
import { IzzyScopeEntity } from 'mj_generatedentities';
import { MJAPIKeyEntity, MJAPIKeyScopeEntity } from '@memberjunction/core-entities';

interface ScopeSelection {
  scope: IzzyScopeEntity;
  isSelected: boolean;
  apiKeyScopeId: string | null;
}

@Component({
  standalone: false,
  selector: 'izzy-api-key-scopes',
  templateUrl: './api-key-scopes.component.html',
  styleUrls: ['./api-key-scopes.component.css']
})
export class APIKeyScopesComponent implements OnInit, OnDestroy {
  @Input() apiKeyId: string | null = null;
  @Input() initialState: any = null;
  @Output() stateChange = new EventEmitter<any>();
  @Output() interaction = new EventEmitter<any>();
  @Output() scopesChanged = new EventEmitter<string[]>();

  constructor() {}

  protected apiKey: MJAPIKeyEntity | null = null;
  protected allScopes: IzzyScopeEntity[] = [];
  protected apiKeyScopes: MJAPIKeyScopeEntity[] = [];
  protected scopeSelections: ScopeSelection[] = [];

  protected isLoading: boolean = true;
  protected isSaving: boolean = false;
  protected errorMessage: string = '';
  protected successMessage: string = '';
  protected searchQuery: string = '';

  private destroy$ = new Subject<void>();

  async ngOnInit(): Promise<void> {
    await this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected async loadData(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = '';

    try {
      // Load all available scopes
      const rvScopes = new RunView();
      const scopesResult = await rvScopes.RunView<IzzyScopeEntity>({
        EntityName: 'Scopes',
        OrderBy: 'Name ASC'
      });
      this.allScopes = scopesResult.Results;

      // If apiKeyId is provided, load the API key and its scopes
      if (this.apiKeyId) {
        const md = new Metadata();
        this.apiKey = await md.GetEntityObject<MJAPIKeyEntity>('MJ: API Keys');
        await this.apiKey.Load(this.apiKeyId);

        const rvApiKeyScopes = new RunView();
        const apiKeyScopesResult = await rvApiKeyScopes.RunView<MJAPIKeyScopeEntity>({
          EntityName: 'MJ: API Key Scopes',
          ExtraFilter: `APIKeyID = '${this.apiKeyId}'`,
          ResultType: 'entity_object'
        });
        this.apiKeyScopes = apiKeyScopesResult.Results;
      }

      // Build scope selections
      this.buildScopeSelections();
    } catch (error) {
      console.error('Error loading data:', error);
      this.errorMessage = 'Failed to load scopes.';
    } finally {
      this.isLoading = false;
    }
  }

  private buildScopeSelections(): void {
    const assignedScopeIds = new Map<string, string>();
    for (const aks of this.apiKeyScopes) {
      assignedScopeIds.set(aks.ScopeID, aks.ID);
    }

    this.scopeSelections = this.allScopes.map(scope => ({
      scope,
      isSelected: assignedScopeIds.has(scope.ID),
      apiKeyScopeId: assignedScopeIds.get(scope.ID) || null
    }));
  }

  get filteredSelections(): ScopeSelection[] {
    return this.scopeSelections.filter(ss => {
      const matchesSearch = !this.searchQuery.trim() ||
        ss.scope.Name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        (ss.scope.Description && ss.scope.Description.toLowerCase().includes(this.searchQuery.toLowerCase()));
      return matchesSearch;
    });
  }

  get selectedCount(): number {
    return this.scopeSelections.filter(ss => ss.isSelected).length;
  }

  async toggleScope(selection: ScopeSelection): Promise<void> {
    if (!this.apiKeyId) {
      // If no API key, just toggle for UI purposes
      selection.isSelected = !selection.isSelected;
      this.emitSelectedScopes();
      return;
    }

    this.isSaving = true;
    try {
      if (selection.isSelected) {
        // Remove scope
        if (selection.apiKeyScopeId) {
          const md = new Metadata();
          const apiKeyScope = await md.GetEntityObject<MJAPIKeyScopeEntity>('MJ: API Key Scopes');
          await apiKeyScope.Load(selection.apiKeyScopeId);
          await apiKeyScope.Delete();
          selection.isSelected = false;
          selection.apiKeyScopeId = null;
          this.showSuccess('Scope removed');
        }
      } else {
        // Add scope
        const md = new Metadata();
        const apiKeyScope = await md.GetEntityObject<MJAPIKeyScopeEntity>('MJ: API Key Scopes');
        apiKeyScope.APIKeyID = this.apiKeyId;
        apiKeyScope.ScopeID = selection.scope.ID;
        await apiKeyScope.Save();
        selection.isSelected = true;
        selection.apiKeyScopeId = apiKeyScope.ID;
        this.showSuccess('Scope added');
      }
      this.emitSelectedScopes();
    } catch (error) {
      console.error('Error toggling scope:', error);
      this.errorMessage = 'Failed to update scope.';
    } finally {
      this.isSaving = false;
    }
  }

  selectAll(): void {
    this.filteredSelections.forEach(ss => {
      if (!ss.isSelected) {
        ss.isSelected = true;
      }
    });
    this.emitSelectedScopes();
  }

  deselectAll(): void {
    this.filteredSelections.forEach(ss => {
      if (ss.isSelected) {
        ss.isSelected = false;
        ss.apiKeyScopeId = null;
      }
    });
    this.emitSelectedScopes();
  }

  private emitSelectedScopes(): void {
    const selectedScopeIds = this.scopeSelections
      .filter(ss => ss.isSelected)
      .map(ss => ss.scope.ID);
    this.scopesChanged.emit(selectedScopeIds);
  }

  getScopeIcon(name: string): string {
    const lowerName = name.toLowerCase();
    if (lowerName.includes('read')) return 'fa-eye';
    if (lowerName.includes('write') || lowerName.includes('create')) return 'fa-pen';
    if (lowerName.includes('delete')) return 'fa-trash';
    if (lowerName.includes('admin')) return 'fa-shield-alt';
    if (lowerName.includes('message')) return 'fa-envelope';
    if (lowerName.includes('channel')) return 'fa-broadcast-tower';
    if (lowerName.includes('org')) return 'fa-building';
    return 'fa-key';
  }

  private showSuccess(message: string): void {
    this.successMessage = message;
    setTimeout(() => { this.successMessage = ''; }, 3000);
  }
}

export function LoadAPIKeyScopesComponent(): void {}
